import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '@card-software/library';
import {
  InventoryAdjustmentDto,
  InventoryAdjustmentInsert,
  InventorySearchCommandBase,
  LotBalanceSummary,
  LotBasedAdjustmentRequest,
  LotBasedAdjustmentResponse,
} from '../../models/inventory/inventory-adjustment';

@Injectable({
  providedIn: 'root',
})
export class InventoryAdjustmentProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/inventory-adjustment';

  /**
   * Gets all inventory adjustments
   */
  public getAll(): Observable<ApiResponse<InventoryAdjustmentDto[]>> {
    return this.httpClient.get<ApiResponse<InventoryAdjustmentDto[]>>(`${this.baseUrl}/all`);
  }

  /**
   * Gets an inventory adjustment by ID
   */
  public getById(id: string): Observable<ApiResponse<InventoryAdjustmentDto>> {
    return this.httpClient.get<ApiResponse<InventoryAdjustmentDto>>(`${this.baseUrl}/${id}`);
  }

  /**
   * Gets an inventory adjustment by number
   */
  public getByNumber(number: string): Observable<ApiResponse<InventoryAdjustmentDto>> {
    return this.httpClient.get<ApiResponse<InventoryAdjustmentDto>>(`${this.baseUrl}/by-number/${encodeURIComponent(number)}`);
  }

  /**
   * Searches inventory adjustments by criteria
   */
  public search(searchCommand: InventorySearchCommandBase): Observable<ApiResponse<InventoryAdjustmentDto[]>> {
    return this.httpClient.post<ApiResponse<InventoryAdjustmentDto[]>>(`${this.baseUrl}/search`, searchCommand);
  }

  /**
   * Creates and executes an inventory adjustment
   */
  public create(command: InventoryAdjustmentInsert): Observable<ApiResponse<InventoryAdjustmentDto>> {
    return this.httpClient.post<ApiResponse<InventoryAdjustmentDto>>(this.baseUrl, command);
  }

  /**
   * Voids an inventory adjustment
   */
  public void(id: string): Observable<ApiResponse<InventoryAdjustmentDto>> {
    return this.httpClient.post<ApiResponse<InventoryAdjustmentDto>>(`${this.baseUrl}/${id}/void`, {});
  }

  /**
   * Gets the current balance for a lot by ProviderLotId
   */
  public getLotBalance(providerLotId: string, locationCode?: string): Observable<ApiResponse<LotBalanceSummary>> {
    // Query string, not a path segment: provider lot ids carry "/" (e.g. "M-2578/QA940").
    let params = new HttpParams().set('providerLotId', providerLotId);
    if (locationCode) {
      params = params.set('locationCode', locationCode);
    }
    return this.httpClient.get<ApiResponse<LotBalanceSummary>>(`${this.baseUrl}/lot/balance`, { params });
  }

  /**
   * Creates a lot-based reduction adjustment
   * Automatically processes lot items using FIFO until the reduction is complete
   */
  public createLotBasedReduction(request: LotBasedAdjustmentRequest): Observable<ApiResponse<LotBasedAdjustmentResponse>> {
    return this.httpClient.post<ApiResponse<LotBasedAdjustmentResponse>>(`${this.baseUrl}/lot/reduction`, request);
  }
}
